import React from 'react';

import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import { tableHelper } from '@builttocreate/engine-utils';

import FieldDisplayTypes from '../../../../constants/FieldDisplayTypes';
import FieldModes from '../../../../constants/FieldModes';
import { mergeRowDecorators } from '../../../../lib/decoratorHelper';
import convertOperandLookupToColumnLookup from '../../../../lib/convertOperandLookupToColumnLookup';
import { filterHiddenTableColumnsSelector, getDecoratorColumnWidth } from '../../../../lib/tableHelper';
import { reconcileTableCollectionLeadingChrome } from '../../../../lib/exporterHelper';
import DecoratorConfigContext from '../../../DecoratorConfigContext';
import CollectionRenderer from '../collectionRenderer/CollectionRenderer';
import FieldTitle from '../FieldTitle';
import FieldDecorators from '../FieldDecorators';
import InputGroupFieldRow from './InputGroupFieldRow';
import TableFieldFooter from './TableFieldFooter';
import TableFieldHeader from './TableFieldHeader';
import TableFieldRow from './TableFieldRow';
import TableFieldRteHeader from './TableFieldRteHeader';
import TableFieldRteRow from './TableFieldRteRow';
import { isRowPopulatedForRte } from '../../../../lib/rteTableHelper';
import Tooltip from '../../../misc/Tooltip';

import TableFieldRteEditRowModal from './TableFieldRteEditRowModal';

const {
  generateTableLookup,
  generateTableLookupForRows,
  getRows,
  sortRows,
  getColumns,
  sortColumns,
  getCleanedRowOrder
} = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

const StyledTable = styled.div`
  display: inline-block;
  border-radius: ${(props) => props.borderRadius}px;
  ${(props) => props.border && `border: ${props.border};`}

  ${(props) => {
    if (props.fieldMode === FieldModes.pdf) {
      return 'overflow: visible;';
    }
    else if (props.measure) {
      return 'overflow-y: visible; overflow-x: auto;';
    } else {
      return 'overflow: auto;';
    }
  }}

  &.original-display {
    #table-field-row:first-child div {
      border-top: 0px;
    }

    #table-field-row:last-child div {
      border-bottom: 0px;
    }

    #table-field-row div div:first-child {
      border-left: 0px;
    }

    #table-field-row div div:last-child {
      border-right: 0px;
    }
  }

`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

class TableFieldv2 extends React.PureComponent {

  static propTypes = {

    fieldMode: PropTypes.string,
    type: PropTypes.string,
    view: PropTypes.string,
    displayType: PropTypes.string,
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,
    enableSystemFilePicker: PropTypes.bool,

    startIndex: PropTypes.number,
    endIndex: PropTypes.number,

    value: PropTypes.array, //Row data
    rowOrder: PropTypes.array,
    tableColumns: PropTypes.array,
    tableColumnOrder: PropTypes.array,
    settings: PropTypes.array,
    onChange: PropTypes.func,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,
    columnTitleTextOverflow: PropTypes.string,

    //Cell styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    height: PropTypes.number,

    //Measurement properties
    measure: PropTypes.bool,
    measuredHeight: PropTypes.number,
    onMeasureCompleted: PropTypes.func,

    decorators: PropTypes.array,
    rowDecorators: PropTypes.array,
    onDecoratorClick: PropTypes.func,
  }

  static contextType = DecoratorConfigContext;

  constructor(props) {
    super(props);

    const { tableColumns, tableColumnOrder } = props;

    this.state = {
      focused: null,
      rowHeights: {},
      columnWidths: null,
      columns: getColumns(sortColumns(tableColumns, tableColumnOrder)),
      allColumnWidthsLoaded: props.displayType === FieldDisplayTypes.inputGroup || props.displayType === FieldDisplayTypes.rte,
      editRowModalOpen: false,
      editRowModalRow: null,
    };

    this.containerRef = React.createRef();

    this.columnWidths = {};
    this.measuredRows = {};

  }

  componentDidUpdate(prevProps, prevState) {

    const { allColumnWidthsLoaded, columns, rowHeights } = this.state;

    const {
      fieldMode,
      displayType,
      value,
      rowOrder,
      tableColumns,
      tableColumnOrder,
      disabled,
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      measuredHeight,
      measure,
      updatedOn,
      measuredOn,
      onMeasureCompleted,

      fieldPositionTableColumns,
      view,
      docFields,

    } = this.props;

    if (!allColumnWidthsLoaded && displayType === FieldDisplayTypes.original) {
      const filteredColumns = filterHiddenTableColumnsSelector({
        columns, fieldPositionColumns: fieldPositionTableColumns, view, mode: fieldMode, docFields
      });
      if (filteredColumns.length === 0) {
        this.setState({ allColumnWidthsLoaded: true, columnWidths: {} });
      }
    }

    /**
     * 1. We only require columnWidths when displayType is original (table display type)
     * 2. We do not require columnWidths when displayType is inputGroup because it is not rendered with columns.
     */
    const columnsReadyForMeasurement = (allColumnWidthsLoaded && displayType === FieldDisplayTypes.original) || (displayType === FieldDisplayTypes.inputGroup) || (displayType === FieldDisplayTypes.rte)

    /**
     * Handle field measuremments
     * 1. Measure for PDF Generation. This requires all the individual rows to be measured.
     * 2. Measure for edit, fill, & readonly modes. This requires the container table height to be measured.
     */
    if (onMeasureCompleted && measure && this.containerRef && columnsReadyForMeasurement) {

      const measuredRowsTotal = Object.keys(rowHeights).length;

      //A remeasure is required if the measuredOn time is less than the updatedOn time. By measuredOn time 
      //and updatedOn time we can determine if a remeasure is required due to a field change.
      const remeasureRequired = measuredOn && updatedOn && (measuredOn < updatedOn);

      const formattedRows = getRowsSelector({ value, rowOrder });
      const calculatedRows = getCalculatedRowsSelector({ rows: formattedRows, columns });
      const items = getFlattenRowsSelector({ displayType, rows: formattedRows, calculatedRows, columns, disabled });

      if (displayType === FieldDisplayTypes.rte && fieldMode === FieldModes.pdf) {
       
        const rteColumns = this.props.settings || [];
        let needsUpdate = false;
       
        items.forEach(item => {
          if (item.type === 'row' && typeof this.measuredRows[item.layoutKey] !== 'number') {
        
            if (!isRowPopulatedForRte(rteColumns, item, this.props.tableColumns)) {
              this.measuredRows[item.layoutKey] = 0;
              needsUpdate = true;
            }
        
          }
        
          if (item.type === 'header' && rteColumns.length === 0 && typeof this.measuredRows['header'] !== 'number') {
            this.measuredRows['header'] = 0;
            needsUpdate = true;
          }
        });

        if (needsUpdate) {
          this.setState({ rowHeights: { ...rowHeights, ...this.measuredRows } });
          return;
        }

      }

      //1. Handle initial field measurement when all rows are measured
      if (measuredRowsTotal === items.length && !measuredOn) {
        this.handleMeasure();
      }
      //2. Measure for edit, fill, & readonly modes. 
      //IMPORTANT NOTE: Do no remove the pdf fieldMode check. This is to prevent the field from being 
      //measured before all rows have properly been measured when it is in pdf mode.
      else if (fieldMode !== FieldModes.pdf && remeasureRequired && measuredRowsTotal === items.length) {
        this.handleMeasure();
      }

    }

    /**
     * Why are we storing the columns in the state? This is to prevent re-rendering of the TableFieldHeader when the columns change.
     * The issue is replated to the notion ticket number NO-1543
     */
    if (tableColumns !== prevProps.tableColumns || tableColumnOrder !== prevProps.tableColumnOrder) {
      this.setState({ columns: getColumns(sortColumns(tableColumns, tableColumnOrder)) });
    }

  }

  handleMeasure = () => {

    const { fieldId, fieldIdentifier, fieldPositionId, onMeasureCompleted, value, rowOrder, type, displayType, disabled } = this.props;
    const { columns, rowHeights } = this.state;

    const formattedRows = getRowsSelector({ value, rowOrder });
    const calculatedRows = getCalculatedRowsSelector({ rows: formattedRows, columns });
    const items = getFlattenRowsSelector({ displayType, rows: formattedRows, calculatedRows, columns, disabled });


    requestAnimationFrame(() => {

      if (typeof this.containerRef?.current?.getBoundingClientRect().height === 'number') {

        const measuredHeight = this.containerRef.current.getBoundingClientRect().height;

        const layouts = items?.map((row) => ({
          type: row.layoutType,
          key: row.layoutKey,
          header: row.header,
          height: rowHeights?.[row.layoutKey],
        }));

        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {
          layouts: reconcileTableCollectionLeadingChrome({ layouts, measuredHeight, fieldType: type, displayType }),
          height: measuredHeight,
          measuredOn: new Date().getTime()
        });
      }
    });
  }

  render() {

    const { allColumnWidthsLoaded, columns } = this.state;


    const {

      fieldId,
      fieldIdentifier,
      fieldMode,
      fieldPositionId,
      displayType,
      decorators,
      onDecoratorClick,

      measure,
      tipTitle,
      tipDescription,
      tipVisible,
      startIndex,
      endIndex,
      disabled,
      layouts,

      title,
      value,
      rowOrder,

      margin,
      borderWidth,
      borderColor,
      borderRadius,

      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

    } = this.props;

    const formattedRows = getRowsSelector({ value, rowOrder });

    const calculatedRows = getCalculatedRowsSelector({ rows: formattedRows, columns });

    const items = getFlattenRowsSelector({ displayType, rows: formattedRows, calculatedRows, columns, disabled });

    let filteredArray = items;
    if (layouts && layouts.length > 0) filteredArray = getFilteredFlattenedRowsSelector({ items, layouts });


    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const isFirstItemFieldTitle = filteredArray[0] && filteredArray[0].type === 'fieldTitle';

    return (
      <StyledWrapper
        {...{
          margin,
          measure
        }}
        data-testid={`${fieldPositionId}-table-field`}
        ref={this.containerRef}
      >
        {isFirstItemFieldTitle && (
          <StyledFieldHeader
            ref={(e) => {
              /**
               * Measure the full leading-chrome wrapper (StyledFieldHeader) rather
               * than only the nested FieldTitle. The wrapper owns the top spacing
               * (padding/gap) that is part of the field's first PDF split unit, so
               * measuring it keeps sum(layouts[]) aligned with the container height.
               */
              if (e) this.handleRowLoadCompleted('title', { height: e.offsetHeight });
            }}
          >
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
              }}
            />
            <FieldDecorators
              decorators={decorators}
              fieldMode={fieldMode}
              fieldId={fieldId}
              fieldIdentifier={fieldIdentifier}
              fieldPositionId={fieldPositionId}
              onDecoratorClick={onDecoratorClick}
            />
          </StyledFieldHeader>
        )}
        {getTooltip()}
        <StyledTable
          className={displayType === FieldDisplayTypes.original ? 'original-display' : undefined}
          {...{
            fieldMode,
            measure,
            borderRadius,
            border: (displayType === FieldDisplayTypes.inputGroup) ? '' : border,
          }}
        >
          <CollectionRenderer
            {...{
              items: filteredArray,
              itemRenderer: this.itemRenderer,
              virtual: !measure,
              startIndex,
              endIndex,
            }}
          />
        </StyledTable>
        {this.state.editRowModalOpen && this.state.editRowModalRow && displayType === FieldDisplayTypes.rte && (
          <TableFieldRteEditRowModal
            isOpen
            rteColumns={this.props.settings || []}
            row={formattedRows.find((r) => r._id === this.state.editRowModalRow._id) || this.state.editRowModalRow}
            tableColumns={this.props.tableColumns}
            fieldPositionTableColumns={this.props.fieldPositionTableColumns}
            fieldMode={fieldMode}
            onRteSettingsBatchUpdate={this.handleRteSettingsBatchUpdate}
            onCellChange={this.handleCellChange}
            onCellBatchChange={this.handleCellBatchChange}
            onCellFileUpload={(rowId, columnId, value, loadingCallback, errorCallback) => this.handleCellFileUpload(this.state.editRowModalRow._id, rowId, columnId, value, loadingCallback, errorCallback)}
            onCellFileBatchUpload={(rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer) => this.handleCellFileBatchUpload(this.state.editRowModalRow._id, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer)}
            onCellFileClick={this.handleCellFileClick}
            onCellFileDelete={this.handleCellFileDelete}
            onCapture={this.props.onCapture}
            enableSystemFilePicker={this.props.enableSystemFilePicker}
            onClose={this.handleEditRowModalClose}
          />
        )}
      </StyledWrapper>
    );
  }

  itemRenderer = ({ item, index }) => {


    const {
      view,
      width,
      type: fieldType,
      displayType,
      fieldMode,
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      disabled,
      enableSystemFilePicker,
      decorators,
      rowDecorators,
      onDecoratorClick,

      title,
      fieldPositionTableColumns,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleFontStyle,
      titleFontWeight,
      titleTextAlign,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextAlign,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,
      columnTitleTextOverflow,

      //Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
      margin,

      onCapture,

    } = this.props;

    const { focused, columnWidths, columns } = this.state;

    const decoratorColumnWidth = getDecoratorColumnWidth(this.context?.visibleLimitInRows || 1);

    const isRte = displayType === FieldDisplayTypes.rte;

    const rowActions = isRte ? [
      { _id: 'editRow', value: 'Edit Row' },
      { _id: 'moveUp', value: 'Move Up' },
      { _id: 'moveDown', value: 'Move Down' },
    ] : [
      { _id: 'addBelow', value: 'Insert Below' },
      { _id: 'duplicate', value: 'Duplicate' },
      { _id: 'moveUp', value: 'Move Up' },
      { _id: 'moveDown', value: 'Move Down' },
      { _id: 'delete', value: 'Delete' },
    ];

    const fieldDecorate = this.props.decorate;
    const enableRowDecorators = (() => {
      if (fieldMode === FieldModes.pdf) return false;
      if (fieldDecorate !== undefined) return fieldDecorate === true;
      if (rowDecorators && rowDecorators.length > 0) return true;
      const rows = this.props.value || [];
      return rows.some((row) => !row.deleted && row.decorators?.all?.length > 0);
    })();

    const handleRowDecoratorClick = enableRowDecorators && onDecoratorClick
      ? (decorator, rowId) => {
        onDecoratorClick(fieldId, fieldIdentifier, fieldPositionId, decorator.action, rowId);
      }
      : null;

    const { _id, type, layoutKey, rowIndex } = item;

    let itemNode = null;

    if (displayType === FieldDisplayTypes.original && type === 'header') {
      itemNode = (
        <TableFieldHeader
          view={view}
          fieldType={fieldType}
          fieldMode={fieldMode}
          docFields={this.props.docFields}
          key={index}
          fieldPositionColumns={fieldPositionTableColumns}
          columns={this.state.columns}

          enableNumber
          enableMenu={!disabled || fieldMode === FieldModes.fill}
          enableRowDecorators={enableRowDecorators}
          decoratorColumnWidth={decoratorColumnWidth}
          {...{
            disabled,
            //Styles
            fontSize: columnTitleFontSize,
            fontColor: columnTitleFontColor,
            textAlign: columnTitleTextAlign,
            fontStyle: columnTitleFontStyle,
            fontWeight: columnTitleFontWeight,
            textTransform: columnTitleTextTransform,
            textDecoration: columnTitleTextDecoration,
            textOverflow: columnTitleTextOverflow,
            backgroundColor: columnTitleBackgroundColor,
            padding: columnTitlePadding,
            borderWidth,
            borderColor,
            onColumnRef: this.handleColumnRef,
            loadCompletedId: 'header',
            onLoadCompleted: this.handleRowLoadCompleted,
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.original && type === 'row') {
      itemNode = (
        <TableFieldRow
          view={view}
          fieldMode={fieldMode}
          docFields={this.props.docFields}
          width={width}
          key={index}
          fieldType={fieldType}
          enableNumber
          enableMenu={!disabled || fieldMode === FieldModes.fill}
          enableRowDecorators={enableRowDecorators}
          decoratorColumnWidth={decoratorColumnWidth}
          rowDecorators={mergeRowDecorators(rowDecorators, item.decorators?.all)}
          onRowDecoratorClick={handleRowDecoratorClick}
          disabled={disabled}
          enableSystemFilePicker={enableSystemFilePicker}
          focused={focused}
          rowIndex={rowIndex}
          fieldPositionColumns={fieldPositionTableColumns}
          columns={columns}
          columnWidths={columnWidths}
          rowPath={_id}
          row={item}
          onCellFocus={this.handleCellFocus}
          onCellBlur={this.handleCellBlur}
          onCellChange={this.handleCellChange}
          onCellFileUpload={(r, c, v, l, e) => this.handleCellFileUpload(layoutKey, r, c, v, l, e)}
          onCellFileClick={this.handleCellFileClick}
          onCellFileDelete={this.handleCellFileDelete}

          onCellKeyPress={this.handleCellKeyPress}
          onCapture={onCapture}
          rowActions={rowActions}
          onRowAction={this.handleRowAction}
          onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
          {...{
            //Styles
            primaryColor,
            dangerColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderWidth,
            backgroundColor,
            padding,
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.original && type === 'footer') {
      itemNode = (
        <TableFieldFooter
          {...{
            disabled,
            //Styles
            backgroundColor: columnTitleBackgroundColor,
            padding: columnTitlePadding,
            borderWidth,
            borderColor,
            onRowCreate: this.handleAddRow,
            loadCompletedId: 'footer',
            onLoadCompleted: this.handleRowLoadCompleted,
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.inputGroup && type === 'row') {

      itemNode = (
        <InputGroupFieldRow
          key={index}
          view={view}
          docFields={this.props.docFields}
          enableTitle
          title={title}
          enableMenu={!disabled || fieldMode === FieldModes.fill}
          enableSystemFilePicker={enableSystemFilePicker}
          fieldType={fieldType}
          disabled={disabled}
          focused={focused}
          rowIndex={rowIndex}
          fieldPositionColumns={fieldPositionTableColumns}
          columns={columns}
          rowPath={_id}
          row={item}
          onCellFocus={this.handleCellFocus}
          onCellBlur={this.handleCellBlur}
          onCellChange={this.handleCellChange}
          onCellFileUpload={(r, c, v, l, e) => this.handleCellFileUpload(layoutKey, r, c, v, l, e)}
          onCellFileClick={this.handleCellFileClick}
          onCellFileDelete={this.handleCellFileDelete}
          onCellKeyPress={this.handleCellKeyPress}
          onCapture={onCapture}
          rowActions={rowActions}
          onRowAction={this.handleRowAction}
          onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
          fieldMode={fieldMode}
          fieldId={fieldId}
          fieldIdentifier={fieldIdentifier}
          fieldPositionId={fieldPositionId}
          decorators={decorators}
          onDecoratorClick={onDecoratorClick}
          enableRowDecorators={enableRowDecorators}
          rowDecorators={mergeRowDecorators(rowDecorators, item.decorators?.all)}
          onRowDecoratorClick={handleRowDecoratorClick}
          {...{
            //Styles
            primaryColor,
            dangerColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,

            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleTextAlign,
            titleFontStyle,
            titleFontWeight,
            titleTextTransform,
            titleTextDecoration,

            columnTitleFontSize,
            columnTitleFontColor,
            columnTitleTextAlign,
            columnTitleFontStyle,
            columnTitleFontWeight,
            columnTitleTextTransform,
            columnTitleTextDecoration,
            columnTitleBackgroundColor,
            columnTitlePadding,
            columnTitleTextOverflow,

          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.inputGroup && type === 'footer') {
      itemNode = (
        <TableFieldFooter
          {...{
            disabled,
            //Styles
            backgroundColor: 'tranpsarent',
            padding: 4,
            borderWidth,
            borderColor,
            onRowCreate: this.handleAddRow,
            loadCompletedId: 'footer',
            onLoadCompleted: this.handleRowLoadCompleted,
          }}
        />
      );
    } else if (displayType === FieldDisplayTypes.rte && type === 'header') {
      const rteColumns = this.props.settings || [];
      if (rteColumns.length === 0 && fieldMode !== FieldModes.edit) {
        itemNode = null;
      } else {
        itemNode = (
          <TableFieldRteHeader
            id={fieldId}
            fieldMode={fieldMode}
            rteColumns={rteColumns}
            tableColumns={this.props.tableColumns}
            enableNumber
            enableMenu={fieldMode === FieldModes.fill}
            {...{
              fontSize: columnTitleFontSize,
              fontColor: columnTitleFontColor,
              fontStyle: columnTitleFontStyle,
              fontWeight: columnTitleFontWeight,
              textAlign: columnTitleTextAlign,
              textTransform: columnTitleTextTransform,
              textDecoration: columnTitleTextDecoration,
              textOverflow: columnTitleTextOverflow,
              backgroundColor: columnTitleBackgroundColor,
              padding: columnTitlePadding,
              borderWidth,
              borderColor,
              loadCompletedId: 'header',
              onLoadCompleted: this.handleRowLoadCompleted,
            }}
          />
        );
      }
    } else if (displayType === FieldDisplayTypes.rte && type === 'row') {
      const rteColumns = this.props.settings || [];
      const rowHasContent = isRowPopulatedForRte(rteColumns, item, this.props.tableColumns);
      if (!rowHasContent && fieldMode !== 'edit') return null;
      itemNode = (
        <TableFieldRteRow
          id={fieldId}
          row={item}
          rowIndex={rowIndex}
          rowPath={_id}
          rteColumns={rteColumns}
          tableColumns={this.props.tableColumns}
          fieldPositionTableColumns={fieldPositionTableColumns}
          fieldMode={fieldMode}
          disabled={disabled}
          enableNumber
          enableMenu={fieldMode === FieldModes.fill}
          rowActions={rowActions}
          onRowAction={this.handleRowAction}
          onLoadCompleted={(r, measurement) => this.handleRowLoadCompleted(layoutKey, measurement)}
          {...{
            primaryColor,
            dangerColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            padding,
            borderWidth,
            borderColor,
            backgroundColor,
          }}
        />
      );
    }

    return itemNode;

  }

  handleRowLoadCompleted = (key, measurement) => {

    const { rowHeights } = this.state;

    /**
     * Important Note: Always need to check for if the measuredRows[key] is a number because it is possible that the key is not yet defined
     * or that the stored value is a zero number. Zero is a valid height so we need to check for this.
     */
    if (typeof this.measuredRows[key] === 'number' && this.measuredRows[key] === measurement.height) return;
    this.measuredRows[key] = measurement.height;

    // Always update rowHeights state when a measurement changes, not just on initial load
    // This ensures that row height changes from image uploads are reflected in state
    // and the remeasure logic can properly detect and handle height changes
    this.setState({ rowHeights: { ...rowHeights, ...this.measuredRows } });

  }

  handleColumnRef = (columnId, columnRef) => {

    const { columns } = this.state;
    const { fieldPositionTableColumns, view } = this.props;

    const filteredColumns = filterHiddenTableColumnsSelector({ columns, fieldPositionColumns: fieldPositionTableColumns, view, mode: this.props.fieldMode, docFields: this.props.docFields });

    if (this.columnWidths[columnId] && this.columnWidths[columnId] === columnRef.offsetWidth) return;
    this.columnWidths[columnId] = columnRef.offsetWidth;

    const totalColumnWidthsLoaded = Object.keys(this.columnWidths).length;

    /**
     * Trigger new column widths only when all the columns are loaded.
     *
     * Why do we allow totalColumnWidthsLoaded to be greater than columns.length?
     * This is to account when a column is removed from the table.
     *
     */
    if (totalColumnWidthsLoaded >= filteredColumns.length) {
      this.setState({ allColumnWidthsLoaded: true, columnWidths: { ...this.columnWidths } });
    }

  }

  handleRowAction = (type, rowId, rowIndex) => {
    if (type === 'editRow') this.handleEditRowModalOpen(rowId, rowIndex);
    else if (type === 'addBelow') this.handleInsertBelowRow(rowId, rowIndex);
    else if (type === 'duplicate') this.handleDuplicateRow(rowId, rowIndex);
    else if (type === 'moveUp') this.handleMoveUpRow(rowId, rowIndex);
    else if (type === 'moveDown') this.handleMoveDownRow(rowId, rowIndex);
    else if (type === 'delete') this.handleDeleteRow(rowId, rowIndex);
  }

  handleEditRowModalOpen = (rowId, rowIndex) => {
    const { value, rowOrder } = this.props;
    const rows = getRowsSelector({ value, rowOrder });
    const row = rows.find((r) => r._id === rowId);
    if (row) this.setState({ editRowModalOpen: true, editRowModalRow: row });
  }

  handleEditRowModalClose = () => {
    this.setState({ editRowModalOpen: false, editRowModalRow: null });
  }

  handleRteSettingsBatchUpdate = (pendingChanges) => {
    const { onChange, fieldId, fieldIdentifier, fieldPositionId, settings } = this.props;
    const nextSettings = settings ? settings.map((s) => ({ ...s })) : [];
    const ids = Object.keys(pendingChanges);
    for (let i = 0; i < ids.length; i++) {
      const index = nextSettings.findIndex((s) => s._id === ids[i]);
      if (index !== -1) {
        nextSettings[index] = { ...nextSettings[index], ...pendingChanges[ids[i]] };
      }
    }
    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, { settings: nextSettings });
  }

  handleAddRow = () => {

    const { onRowCreate, fieldId, fieldIdentifier, fieldPositionId, value, rowOrder } = this.props;

    const rows = getRowsSelector({ value, rowOrder });

    if (onRowCreate) onRowCreate(fieldId, fieldIdentifier, fieldPositionId, rows.length - 1);

  }

  handleInsertBelowRow = (rowId, rowIndex) => {

    const { onRowAddBelow, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowAddBelow) onRowAddBelow(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleDuplicateRow = (rowId, rowIndex) => {
    const { onRowDuplicate, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowDuplicate) onRowDuplicate(fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex);
  }

  handleMoveUpRow = (rowId, rowIndex) => {

    const { onRowMoveUp, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rowIndex === 0) return;

    if (onRowMoveUp) onRowMoveUp(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleMoveDownRow = (rowId, rowIndex) => {

    const { onRowMoveDown, value, rowOrder, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const rows = getRowsSelector({ value, rowOrder });

    if (rows.length === 1 || (rows.length - 1) === rowIndex) return;

    if (onRowMoveDown) onRowMoveDown(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleDeleteRow = (rowId, rowIndex) => {

    const { onRowDelete, fieldId, fieldIdentifier, fieldPositionId, value, rowOrder } = this.props;

    const rows = getRowsSelector({ value, rowOrder });

    if (rows.length === 1) return;

    if (onRowDelete) onRowDelete(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleCellChange = (rowId, columnId, value, timezone, signer) => {

    const { onCellChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onCellChange) onCellChange(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value, timezone, signer);

  }

  handleCellBatchChange = (rowId, cellChanges, timezone, signer) => {

    const { onCellBatchChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onCellBatchChange) onCellBatchChange(fieldId, fieldIdentifier, fieldPositionId, rowId, cellChanges, timezone, signer);

  }

  handleCellFileUpload = (layoutKey, rowId, columnId, value, loadingCallback, errorCallback) => {

    const { onCellUpload, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    /**
     * >>>>>>>> START ROW LOAD RESET LOGIC >>>>>>>>
     * 
     * This logic resets the rowHeights and measuredRows states for a specific row.
     * This ensures that the row height is properly reset and the remeasure logic can properly detect 
     * and handle height changes. This is usually in response to an image being uploaded.
     * 
     * Without this logic the row height will not be properly reset and the row 
     * will not be properly measured.
     */
    const { rowHeights } = this.state;

    delete rowHeights[layoutKey];
    delete this.measuredRows[layoutKey];

    this.setState({ rowHeights: { ...rowHeights } });
    //<<<<<<<< END ROW LOAD RESET LOGIC <<<<<<<<

    if (onCellUpload) onCellUpload(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value, loadingCallback, errorCallback);

  }

  handleCellFileBatchUpload = (layoutKey, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer) => {

    const { onCellBatchUpload, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const { rowHeights } = this.state;

    delete rowHeights[layoutKey];
    delete this.measuredRows[layoutKey];

    this.setState({ rowHeights: { ...rowHeights } });

    if (onCellBatchUpload) onCellBatchUpload(fieldId, fieldIdentifier, fieldPositionId, rowId, columnUploads, loadingCallback, errorCallback, batchedCells, timezone, signer);

  }

  handleCellFileClick = (rowId, columnId, fileObject, fileIndex) => {

    const { onFileClick, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onFileClick) onFileClick(fieldId, fieldIdentifier, fieldPositionId, fileObject, fileIndex, { rowId, columnId });

  }

  handleCellFileDelete = (rowId, columnId, fileObject, fileIndex) => {

    const { onFileDelete, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onFileDelete) onFileDelete(fieldId, fieldIdentifier, fieldPositionId, fileObject, fileIndex, { rowId, columnId });

  }

  handleCellFocus = (rowId, columnId, columnIdentifier, columnType, e) => {

    const { focused } = this.state;

    const { onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const focusedKey = `${rowId}_${columnId}`;

    /**<---------------------------NOTICE------------------------------>
     * Why do we use columnTypes here to trigger onFocus?
     *
     * We use columnTypes to trigger onFocus only when the focused components of a tableField
     * (ie StyledSelect/StyledTextArea) have been rendered so that when user triggers e.blur
     * within the blurFunction of onFocus event we can the blur these focused components.
     * We will only have access to the focused component's event object when they are rendered.
     *
     * Table cells use two sets of components to display data.
     * 1) If a cell is focused StyledSelect or StyledTextarea is used.
     * 2) If a cell is not focused a simple tableDataCell is used.
     *
     * handleCellFocus will be triggered twice everytime a cell is focused.
     *
     * The First call to handleCellFocus:
     * 1. When a table cell is focused for the first time,  TableDataCell will be clicked triggering
     *    handleCellFocus and the focused cell's rowId and columnId  will be set as the focused in state.
     *    ie: this.setState({ focused: `${rowId}_${columnId}` });
     *
     * 2. We should NOT TRIGGER onFocus during the first call. If we do so, we won't be able to blur
     *    the StyledSelect/StyledTextarea when user triggers e.blur since we would not have access to
     *    the event object associated with StyledSelect/StyledTextarea as they are yet to be rendered.
     *
     * 3. Once focused is set in state StyledTextArea or StyledSelectCell will replace TableDataCell.
     *
     * The Second call to handleCellFocus:
     * 1. Once StyledSelect or StyledTextarea is rendered autoFocus prop on them triggers handleCellFocus again.
     * 2. We will only pass columnType to handleCellFocus now and we SHOULD TRIGGER onFocus during this since
     *    we will be able to blur these focused components using e.target.blur when user triggers e.blur from the
     *    blurFunction due to the availablity of the event object associated with StyledSelect/StyledTextarea
     *
     */

    if (onFocus && columnType) {

      const blurFunction = e && e.blur ? {
        blur: () => {
          e.blur();
        }
      } : {};

      onFocus(fieldId, fieldIdentifier, fieldPositionId, blurFunction, rowId, columnId, columnIdentifier);

    }

    if (focusedKey === focused) return;

    this.setState({ focused: `${rowId}_${columnId}` });

  }

  handleCellBlur = (rowId, columnId, columnIdentifier) => {

    const { onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    this.setState({ focused: null });

    if (onBlur) {

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, columnId, columnIdentifier);

    }

  }

  handleCellKeyPress = (rowId, columnId, event) => {

    const { focused } = this.state;
    const { value, rowOrder, tableColumnOrder, tableColumns } = this.props;

    const tableMap = getTableNavigationMapSelector({
      rows: getRowsSelector({ value, rowOrder }),
      columns: getColumnsSelector({ tableColumns, tableColumnOrder })
    });

    const keyCode = event.keyCode || event.charCode;

    // Shift Tab key
    if (keyCode === 9 && event.shiftKey) {

      event.preventDefault();
      event.target.blur();

      const currentCellIndex = tableMap.indexOf(focused);

      if (currentCellIndex === 0) return;

      const nextCellIndex = tableMap[currentCellIndex - 1];

      this.setState({ focused: nextCellIndex });

    }

    // Tab key
    else if (keyCode === 9) {

      event.preventDefault();
      event.target.blur();

      const currentCellIndex = tableMap.indexOf(focused);

      if (currentCellIndex === (tableMap.length - 1)) return;

      const nextCellIndex = tableMap[currentCellIndex + 1];

      this.setState({ focused: nextCellIndex });
    }

    // escape
    else if (keyCode === 27) {

      event.preventDefault();
      event.target.blur();

      this.setState({ focused: null });

    }

  }

  getTableNavigationMap = (rows, columns) => {

    const tableMaps = [];

    rows.forEach((row) => {
      columns.forEach((column) => {
        if (column._id !== 'rowNumber' && column._id !== 'rowMenu') {
          tableMaps.push(`${row._id}_${column._id}`);
        }
      });
    });

    return tableMaps;

  }

}

const getTableNavigationMapSelector = createSelector(
  (state) => state.rows, //Should be rows returned from getRowsSelector
  (state) => state.columns, //Should be columns returned from getColumnsSelector
  (rows, columns) => {

    const tableMaps = [];

    rows.forEach((row) => {
      columns.forEach((column) => {
        if (column._id !== 'rowNumber' && column._id !== 'rowMenu') {
          tableMaps.push(`${row._id}_${column._id}`);
        }
      });
    });

    return tableMaps;

  }
);

const getFilteredFlattenedRowsSelector = createSelector(
  (state) => state.items,
  (state) => state.layouts,
  (items, layouts) => {

    const lookup = Object.create(null);
    for (const l of layouts) lookup[l.key] = true;

    const result = [];
    for (const item of items) if (lookup[item.layoutKey]) result.push(item);
    return result;

  }
);

const getFlattenRowsSelector = createSelector(
  (state) => state.displayType,
  (state) => state.rows, //Should be rows returned from getRowsSelector
  (state) => state.calculatedRows, //Should be rows returned from getCalculatedRowsSelector
  (state) => state.disabled,
  (displayType, rows, calculatedRows, disabled) => {

    const flattenedArray = [];

    /**
     * layoutType and layoutKey are used to handle
     * PDF Rendering, Page Breaks and Measurement
     * management
     */
    const showHeaderAndTitle = displayType !== FieldDisplayTypes.inputGroup;
    if (showHeaderAndTitle) flattenedArray.push({ type: 'fieldTitle', layoutType: 'title', layoutKey: 'title' });
    if (showHeaderAndTitle) flattenedArray.push({ type: 'header', layoutType: 'header', layoutKey: 'header' });

    rows.forEach((row, rowIndex) => {
      const calculatedCells = calculatedRows && calculatedRows[rowIndex] ? calculatedRows[rowIndex] : {};
      flattenedArray.push({
        ...row,
        header: 'header', //Used by PDF render to identify associated header and perform repeating header logic
        type: 'row',
        calculatedCells,
        rowIndex,
        layoutType: 'row',
        layoutKey: row._id,
      });
    });

    /**
     * Footer does not contain layoutType or layoutKey because
     * it is not rendering duering PDF rendering.
     */
    if (!disabled && displayType !== FieldDisplayTypes.rte) flattenedArray.push({ type: 'footer' });

    return flattenedArray;

  }
);

const getColumnsSelector = createSelector(
  (state) => state.tableColumns,
  (state) => state.tableColumnOrder,
  (tableColumns, tableColumnOrder) => {
    return getColumns(sortColumns(tableColumns, tableColumnOrder));
  }
);

const getRowsSelector = createSelector(
  (state) => state.value,
  (state) => state.rowOrder,
  (rows, rowOrder) => {

    /**
     * getCleanedRowOrder returns a cleanedRowOrder after removing duplicate rowIds/ deletedRowIds.
     * If the rowOrder is passed as an empty array, we would generate a rowOrder based on
     * the rows that were passed via the input.
     */

    const cleanedRowOrder = getCleanedRowOrder(rowOrder, rows);

    return sortRows(getRows(rows), cleanedRowOrder).map((row, rowIndex) => ({ ...row, rowIndex }));

  }
);

const getCalculatedRowsSelector = createSelector(
  (state) => state.rows, //Should be rows returned from getRowsSelector
  (state) => state.columns, //Should be columns returned from getColumnsSelector
  (rows, columns) => {

    const tableLookup = generateTableLookup(rows, columns);
    const rowTableLookup = generateTableLookupForRows(rows, columns, tableLookup);


    const calculatedRowsByIndex = {};

    rows.forEach((row, rowIndex) => {
      calculatedRowsByIndex[rowIndex] = convertOperandLookupToColumnLookup(rowTableLookup[rowIndex + 1], columns); //RowTableLookup starts at 1 instead of 0
    });

    return calculatedRowsByIndex;

  }
);

export default TableFieldv2;
